import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getNews, createNews } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Newspaper, Megaphone, HeartPulse, Calendar, Loader2, Plus, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const NewsPage = () => {
  const { language } = useLanguage();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Submission form state
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [newNews, setNewNews] = useState({
    title_en: '', title_pa: '', content_en: '', content_pa: '', type: 'news', date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const data = await getNews(20);
        setNewsItems(data);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'announcement': return Megaphone;
      case 'news': return Newspaper;
      case 'health': return HeartPulse;
      default: return Newspaper;
    }
  };

  const getTypeStyle = (type) => {
    switch (type) {
      case 'announcement': return 'bg-blue-100 text-blue-600';
      case 'news': return 'bg-emerald-100 text-emerald-600';
      case 'health': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      announcement: { en: 'Announcement', pa: 'ਐਲਾਨ' },
      news: { en: 'News', pa: 'ਖ਼ਬਰ' },
      health: { en: 'Health', pa: 'ਸਿਹਤ' }
    };
    return labels[type] ? labels[type][language] : type;
  };

  const getText = (item, field) => {
    return language === 'pa' ? item[`${field}_pa`] : item[`${field}_en`];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newNews.title_en || !newNews.content_en) {
      toast.error(language === 'pa' ? 'ਸਿਰਲੇਖ ਅਤੇ ਸਮੱਗਰੀ ਭਰੋ' : 'Please fill title and content');
      return;
    }
    setSubmitting(true);
    try {
      await createNews({
        ...newNews,
        title_pa: newNews.title_pa || newNews.title_en,
        content_pa: newNews.content_pa || newNews.content_en,
        date: new Date(newNews.date).toISOString()
      }, false); // false = public submission (pending)
      setSubmitted(true);
      setTimeout(() => {
        setSubmitOpen(false);
        setSubmitted(false);
        setNewNews({ title_en: '', title_pa: '', content_en: '', content_pa: '', type: 'news', date: new Date().toISOString().split('T')[0] });
      }, 2000);
    } catch (error) {
      toast.error(language === 'pa' ? 'ਗਲਤੀ ਹੋਈ' : 'Error submitting');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {language === 'pa' ? 'ਖ਼ਬਰਾਂ ਅਤੇ ਐਲਾਨ' : 'News & Announcements'}
            </h1>
            <p className="text-amber-100">
              {language === 'pa' ? 'ਪਿੰਡ ਦੀਆਂ ਤਾਜ਼ਾ ਖ਼ਬਰਾਂ' : 'Latest updates from the village'}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          </div>
        ) : (
          <div className="space-y-6">
            {newsItems.map((news) => {
              const Icon = getIcon(news.type);
              
              return (
                <Card key={news.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      <div className={`w-full md:w-48 p-6 flex flex-col items-center justify-center ${getTypeStyle(news.type)}`}>
                        <Icon className="h-8 w-8 mb-2" />
                        <span className="text-sm font-medium">{getTypeLabel(news.type)}</span>
                      </div>
                      <div className="flex-1 p-6">
                        <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                          <Calendar className="h-4 w-4" />
                          {new Date(news.date).toLocaleDateString(language === 'pa' ? 'pa-IN' : 'en-IN', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-3">
                          {getText(news, 'title')}
                        </h2>
                        <p className="text-gray-600 leading-relaxed">
                          {getText(news, 'content')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {!loading && newsItems.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Newspaper className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {language === 'pa' ? 'ਕੋਈ ਖ਼ਬਰ ਨਹੀਂ' : 'No news available'}
            </h3>
          </div>
        )}
      </div>

      {/* Floating Add Button */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogTrigger asChild>
          <Button 
            className="fixed bottom-20 right-6 w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-600 shadow-lg hover:shadow-xl transition-all duration-300 z-50"
            size="icon"
          >
            <Plus className="h-6 w-6" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {language === 'pa' ? 'ਸਫਲਤਾਪੂਰਵਕ ਜਮ੍ਹਾਂ!' : 'Submitted Successfully!'}
              </h3>
              <p className="text-gray-500 text-sm">
                {language === 'pa' 
                  ? 'ਐਡਮਿਨ ਦੀ ਮਨਜ਼ੂਰੀ ਤੋਂ ਬਾਅਦ ਦਿਖਾਈ ਦੇਵੇਗਾ'
                  : 'Will be visible after admin approval'}
              </p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  {language === 'pa' ? 'ਖ਼ਬਰ ਸ਼ਾਮਲ ਕਰੋ' : 'Submit News'}
                </DialogTitle>
                <DialogDescription>
                  {language === 'pa' 
                    ? 'ਐਡਮਿਨ ਮਨਜ਼ੂਰੀ ਤੋਂ ਬਾਅਦ ਹੀ ਦਿਖਾਈ ਦੇਵੇਗਾ'
                    : 'News will appear after admin approval'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <Input 
                  placeholder={language === 'pa' ? 'ਸਿਰਲੇਖ (ਅੰਗਰੇਜ਼ੀ) *' : 'Title (English) *'}
                  value={newNews.title_en}
                  onChange={(e) => setNewNews({...newNews, title_en: e.target.value})}
                />
                <Input 
                  placeholder={language === 'pa' ? 'ਸਿਰਲੇਖ (ਪੰਜਾਬੀ)' : 'Title (Punjabi)'}
                  value={newNews.title_pa}
                  onChange={(e) => setNewNews({...newNews, title_pa: e.target.value})}
                />
                <Select 
                  value={newNews.type} 
                  onValueChange={(val) => setNewNews({...newNews, type: val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'pa' ? 'ਕਿਸਮ ਚੁਣੋ' : 'Select Type'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="news">{language === 'pa' ? 'ਖ਼ਬਰ' : 'News'}</SelectItem>
                    <SelectItem value="announcement">{language === 'pa' ? 'ਐਲਾਨ' : 'Announcement'}</SelectItem>
                    <SelectItem value="health">{language === 'pa' ? 'ਸਿਹਤ' : 'Health'}</SelectItem>
                  </SelectContent>
                </Select>
                <Input 
                  type="date"
                  value={newNews.date}
                  onChange={(e) => setNewNews({...newNews, date: e.target.value})}
                />
                <Textarea 
                  placeholder={language === 'pa' ? 'ਸਮੱਗਰੀ (ਅੰਗਰੇਜ਼ੀ) *' : 'Content (English) *'}
                  value={newNews.content_en}
                  onChange={(e) => setNewNews({...newNews, content_en: e.target.value})}
                  rows={3}
                />
                <Textarea 
                  placeholder={language === 'pa' ? 'ਸਮੱਗਰੀ (ਪੰਜਾਬੀ)' : 'Content (Punjabi)'}
                  value={newNews.content_pa}
                  onChange={(e) => setNewNews({...newNews, content_pa: e.target.value})}
                  rows={3}
                />
                <div className="flex gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setSubmitOpen(false)}
                  >
                    {language === 'pa' ? 'ਰੱਦ ਕਰੋ' : 'Cancel'}
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-amber-500 hover:bg-amber-600"
                    disabled={submitting}
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'pa' ? 'ਜਮ੍ਹਾਂ ਕਰੋ' : 'Submit')}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsPage;
